/**
 * Tickets (TMMPDM-0109; TMMAPI-0181 U5): every tenant's support cases and feature requests, for the
 * product admin to triage. One tab per type (URL `tab`); the status, tenant, text and created-range
 * filters and the page live in the URL too. "Any time" sends no date. The list is server-paged,
 * newest first, and each row links to its ticket. Resellers don't see tickets (v1).
 *
 * Until tmm-api deploys U5 the list answers a bare 404, and the tab says "Tickets aren't
 * available yet." with no retry. Statuses, followers and duplicates are shown as sent.
 *
 * TMMPDM-0110: the page is "Support" (nav and title; the path stays `/tickets`, so old links work)
 * with a third tab, Announcements (`AnnouncementsList`). The tabs stay up when the tickets answer
 * 404, so the announcements are still reachable. Switching to or from Announcements clears the other
 * tab's filters; between the two ticket tabs only the status (each type's own) is cleared.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { useReturnTo } from '@/hooks/useReturnTo';
import {
  oneOf,
  text,
  useSearchDraft,
  useUrlState,
  positiveInt,
  type UrlSchema,
} from '@/hooks/useUrlState';
import { adminApi } from '@/api/tmm/admin';
import { isActingAdminRevoked } from '@/api/errors';
import type { TicketPage } from '@/api/tmm/types';
import { EmptyState, ErrorState, PageHeader } from '@/components/page';
import { AnnouncementsList } from './AnnouncementsList';
import { FilterTabs, stickyTableContainerSx } from '@/components/ui';
import { formatInstant, formatNumber } from '@/format';
import { StatusChip } from '@/status';
import {
  CREATED_RANGES,
  TAB_TYPE,
  TICKET_TABS,
  createdFrom,
  isNotDeployed,
  notAvailableText,
  requesterOf,
  statusLabel,
  statusesOf,
  tenantNameOf,
  ticketRef,
  type CreatedRange,
  type TicketTab,
} from './tickets';

const PAGE_SIZE = 20;
/** The tenant picker lists the tenants the console already lists (one page of up to 200). */
const TENANT_PAGE_SIZE = 200;

type ListView = {
  tab: TicketTab;
  status: string;
  tenant: string;
  q: string;
  created: CreatedRange;
  page: number;
};

const VIEW: UrlSchema<ListView> = {
  tab: { default: 'support', parse: oneOf(TICKET_TABS) },
  status: { default: '', parse: text },
  tenant: { default: '', parse: text },
  q: { default: '', parse: text },
  created: { default: 'ANY', parse: oneOf(CREATED_RANGES) },
  page: { default: 1, parse: positiveInt },
};

const headerCellSx = { fontWeight: 600 } as const;

const SUPPORT_TABS = [...TICKET_TABS, 'announcements'] as const;
type SupportTab = (typeof SUPPORT_TABS)[number];

/** The keys each tab keeps in the URL: changing tabs clears the other tab's. */
const SHELL: UrlSchema<{
  tab: SupportTab;
  status: string;
  tenant: string;
  q: string;
  created: string;
  state: string;
  kind: string;
}> = {
  tab: { default: 'support', parse: oneOf(SUPPORT_TABS) },
  status: { default: '', parse: text },
  tenant: { default: '', parse: text },
  q: { default: '', parse: text },
  created: { default: '', parse: text },
  state: { default: '', parse: text },
  kind: { default: '', parse: text },
};

export function TicketsPage() {
  const { t } = useCopy();
  const [view, setView] = useUrlState(SHELL);
  const announcements = view.tab === 'announcements';
  return (
    <Box>
      <PageHeader
        title={t('page.tickets.title', 'Support')}
        subtitle={t(
          'tickets.subtitle',
          "Support cases, feature requests and announcements for every tenant's members.",
        )}
      />
      <Box sx={{ mb: 2 }}>
        <FilterTabs
          ariaLabel={t('tickets.tabsAria', 'Support sections')}
          active={view.tab}
          onChange={(key) => {
            const tab = oneOf(SUPPORT_TABS)(key);
            if (!tab || tab === view.tab) return;
            // The status filter is the type's own: a new tab starts without one. Announcements share
            // no filter with the tickets.
            if (tab === 'announcements' || announcements) {
              setView({ tab, status: '', tenant: '', q: '', created: '', state: '', kind: '' });
            } else {
              setView({ tab, status: '' });
            }
          }}
          tabs={[
            { key: 'support', label: t('tickets.tab.support', 'Support cases') },
            { key: 'features', label: t('tickets.tab.features', 'Feature requests') },
            { key: 'announcements', label: t('tickets.tab.announcements', 'Announcements') },
          ]}
        />
      </Box>
      {announcements ? <AnnouncementsList /> : <TicketList />}
    </Box>
  );
}

function TicketList() {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const { linkState } = useReturnTo();
  const [view, setView] = useUrlState(VIEW);
  const type = TAB_TYPE[view.tab];
  const statuses = statusesOf(type);
  // A status from the other type's set (a hand-edited link) is no filter on this tab.
  const status = statuses.includes(view.status) ? view.status : '';
  const page = view.page - 1;
  const search = useSearchDraft(view.q, (q) => setView({ q }, { history: 'replace' }));
  const [reloadToken, setReloadToken] = useState(0);
  const [tenants, setTenants] = useState<Array<{ slug: string; name: string }>>([]);
  const viewKey = `${type}:${status}:${view.tenant}:${view.q}:${view.created}:${page}`;
  const requestKey = `${viewKey}:${reloadToken}`;
  const [response, setResponse] = useState<{
    requestKey: string | null;
    result: TicketPage | null;
    failure: unknown;
  }>({ requestKey: null, result: null, failure: null });

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listTickets({
        type,
        ...(status ? { status } : {}),
        ...(view.tenant ? { tenant: view.tenant } : {}),
        ...(view.q ? { q: view.q } : {}),
        ...(view.created === 'ANY' ? {} : { from: createdFrom(view.created) }),
        page,
        size: PAGE_SIZE,
      })
      .then((result) => {
        if (!cancelled) setResponse({ requestKey, result, failure: null });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setResponse({ requestKey, result: null, failure: failure ?? 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [type, status, view.tenant, view.q, view.created, page, requestKey]);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listTenants({ size: TENANT_PAGE_SIZE })
      .then((list) => {
        if (!cancelled)
          setTenants(list.tenants.map((tenant) => ({ slug: tenant.slug, name: tenant.name })));
      })
      .catch(() => {
        // The picker keeps "All tenants"; the list itself still loads.
        if (!cancelled) setTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tenantNames = useMemo(
    () => new Map(tenants.map((tenant) => [tenant.slug, tenant.name])),
    [tenants],
  );

  const current = response.requestKey === requestKey;
  const loading = !current;
  const failure = current ? response.failure : null;
  const result = current ? response.result : null;
  const rows = result?.tickets ?? [];
  const totalPages = result?.totalPages ?? 0;
  const pageNumber = (result?.page ?? page) + 1;
  const features = type === 'FEATURE_REQUEST';
  const columns = features ? 8 : 7;
  const filtered =
    status !== '' || view.tenant !== '' || view.q !== '' || view.created !== 'ANY' || view.page > 1;
  const clearFilters = () => {
    search.discard();
    setView({ status: '', tenant: '', q: '', created: 'ANY', page: 1 });
  };
  const notDeployed = failure !== null && isNotDeployed(failure);

  if (notDeployed) {
    return <EmptyState surface title={notAvailableText(t)} />;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        useFlexGap
        sx={{ mb: 2, flexWrap: 'wrap', alignItems: { xs: 'stretch', md: 'flex-start' } }}
      >
        <TextField
          size="small"
          value={search.value}
          onChange={(event) => search.onChange(event.target.value)}
          label={t('tickets.filter.search', 'Search tickets')}
          helperText={t('tickets.filter.searchHint', 'Searches the title and description')}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label={t('tickets.filter.status', 'Status')}
          value={status === '' ? 'ALL' : status}
          onChange={(event) =>
            setView({ status: event.target.value === 'ALL' ? '' : event.target.value })
          }
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="ALL">{t('tickets.filter.allStatuses', 'All statuses')}</MenuItem>
          {statuses.map((code) => (
            <MenuItem key={code} value={code}>
              {statusLabel(code, t)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('tickets.filter.tenant', 'Tenant')}
          value={view.tenant === '' ? 'ALL' : view.tenant}
          onChange={(event) =>
            setView({ tenant: event.target.value === 'ALL' ? '' : event.target.value })
          }
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="ALL">{t('tickets.filter.allTenants', 'All tenants')}</MenuItem>
          {/* A tenant in the link that the picker doesn't list still reads as itself. */}
          {view.tenant !== '' && !tenantNames.has(view.tenant) ? (
            <MenuItem value={view.tenant}>{view.tenant}</MenuItem>
          ) : null}
          {tenants.map((tenant) => (
            <MenuItem key={tenant.slug} value={tenant.slug}>
              {tenant.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('tickets.filter.created', 'Created')}
          value={view.created}
          onChange={(event) => {
            const created = oneOf(CREATED_RANGES)(event.target.value);
            if (created) setView({ created });
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="ANY">{t('tickets.created.ANY', 'Any time')}</MenuItem>
          <MenuItem value="LAST_7_DAYS">{t('tickets.created.LAST_7_DAYS', 'Last 7 days')}</MenuItem>
          <MenuItem value="LAST_30_DAYS">
            {t('tickets.created.LAST_30_DAYS', 'Last 30 days')}
          </MenuItem>
          <MenuItem value="LAST_90_DAYS">
            {t('tickets.created.LAST_90_DAYS', 'Last 90 days')}
          </MenuItem>
        </TextField>
      </Stack>

      {failure ? (
        <ErrorState
          error={failure}
          message={t('tickets.loadError', 'Could not load tickets.')}
          {...(isActingAdminRevoked(failure)
            ? {}
            : { onRetry: () => setReloadToken((n) => n + 1) })}
        />
      ) : !loading && rows.length === 0 ? (
        <EmptyState
          surface
          title={
            features
              ? t('tickets.empty.features', 'No feature requests.')
              : t('tickets.empty.support', 'No support cases.')
          }
          {...(filtered ? { filtered: { onClear: clearFilters } } : {})}
        />
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          role="region"
          aria-label={t('tickets.regionAria', 'Tickets list')}
          aria-busy={loading ? 'true' : 'false'}
          sx={stickyTableContainerSx}
        >
          <Table
            size="small"
            stickyHeader
            aria-label={
              features
                ? t('tickets.tab.features', 'Feature requests')
                : t('tickets.tab.support', 'Support cases')
            }
          >
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>{t('tickets.col.number', 'Number')}</TableCell>
                <TableCell sx={headerCellSx}>{t('tickets.col.title', 'Title')}</TableCell>
                <TableCell sx={headerCellSx}>{t('tickets.col.tenant', 'Tenant')}</TableCell>
                <TableCell sx={headerCellSx}>{t('tickets.col.requester', 'Requester')}</TableCell>
                <TableCell sx={headerCellSx}>{t('tickets.col.status', 'Status')}</TableCell>
                {features ? (
                  <TableCell sx={headerCellSx} align="right">
                    {t('tickets.col.followers', 'Followers')}
                  </TableCell>
                ) : null}
                <TableCell sx={headerCellSx}>{t('tickets.col.created', 'Created')}</TableCell>
                <TableCell sx={headerCellSx}>{t('tickets.col.updated', 'Updated')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('common.loading', 'Loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const ref = ticketRef(row.type, row.number);
                  return (
                    <TableRow key={row.number} hover>
                      <TableCell sx={(theme) => ({ ...theme.typography.mono })}>
                        <Link
                          component={RouterLink}
                          to={`/tickets/${encodeURIComponent(ref)}`}
                          state={linkState}
                        >
                          {ref}
                        </Link>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}>
                        {row.title}
                      </TableCell>
                      <TableCell>{tenantNameOf(row, tenantNames)}</TableCell>
                      <TableCell sx={{ overflowWrap: 'anywhere' }}>{requesterOf(row)}</TableCell>
                      <TableCell>
                        <StatusChip status={row.status} label={statusLabel(row.status, t)} />
                      </TableCell>
                      {features ? (
                        <TableCell align="right">
                          {row.followers === undefined ? '—' : formatNumber(row.followers, locale)}
                        </TableCell>
                      ) : null}
                      <TableCell>{formatInstant(row.createdAt, locale)}</TableCell>
                      <TableCell>{formatInstant(row.updatedAt, locale)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!failure && (loading || rows.length > 0) ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2, alignItems: 'center', justifyContent: 'flex-end' }}
        >
          <Button
            variant="outlined"
            disabled={loading || (result?.page ?? page) <= 0}
            onClick={() => setView({ page: Math.max(1, view.page - 1) })}
          >
            {t('tenants.pagination.previous', 'Previous')}
          </Button>
          <Typography variant="body2" aria-live="polite">
            {totalPages === 0
              ? null
              : t('tenants.pagination.pageOf', 'Page {{page, number}} of {{total, number}}', {
                  page: pageNumber,
                  total: totalPages,
                })}
          </Typography>
          <Button
            variant="outlined"
            disabled={loading || totalPages === 0 || (result?.page ?? page) + 1 >= totalPages}
            onClick={() => setView({ page: view.page + 1 })}
          >
            {t('tenants.pagination.next', 'Next')}
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}
